import FetchHelper from '../helpers/fetch_helper';
import ApiRequestHelper from '../helpers/api_request_helper';

import {
  applicant,
  applicantEligibility,
  coApplicant,
  coApplicantEligibility,
} from '../components/customer/config/form_fields';
import FunctionalityAccessHelper from '../helpers/functionality_access_helper';

import EligiblityHelper from '../helpers/eligibility_helper';
import DateHelper from '../helpers/date_helper';
import ObjectHelper from '../helpers/object_helper';
import Constants from '../helpers/constants';
import LocationHelper from '../helpers/location_helper';
import AuthHelper from '../helpers/auth_helper';

const {
  SERVICE_ADDRESS_TYPES: { RESIDENTIAL },
} = Constants;

const CustomerDispatcher = {
  clearEligibility(data) {
    // customer, proposal and financingMethod not needed to be deleted.
    // financingMethod - if deleted, will always fetches new FM and end up in primary product rather than selected product tab.
    let currentView;
    if (data?.reload) {
      currentView = this.getStoreVal('view');
      this.setStoreVal('view', { page: 'reloading' });
    }
    this.setStoreVal('eligibility', null);
    this.setStoreVal('eligibilitySection', null);
    this.setStoreVal('legalDocuments', []);
    this.setStoreVal('stipStatusNotesArr', []);
    this.setStoreVal('prescreenProducts', null);
    this.setStoreVal('propertyInfo', null);
    this.setStoreVal('creditDocuments', null);
    if (data?.reload) {
      this.setStoreVal('view', currentView);
    }
  },

  clearCustomer() {
    this.setStoreVal('customer', null);
    this.setStoreVal('customerFormValid', null);
  },

  async fetchCustomer(customerId) {
    try {
      if (customerId === undefined) {
        await this.dispatch({ type: 'fetchProposal' });
        const proposal = this.getStoreVal('proposal');
        customerId = proposal.customerId;
      }
      const customerResponse = await FetchHelper.fetchJson(`customers/${customerId}`, {
        method: 'GET',
        version: '/v3',
      });

      if (customerResponse && customerResponse.results) {
        this.dispatch({
          type: 'setCustomerResults',
          data: customerResponse.results,
        });
      }
      return customerResponse?.results?.[0];
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not retrieve customer' },
      });
    }
  },

  async fetchCustomerEvents(customerId) {
    try {
      const webhookEventsResponse = await FetchHelper.fetchJson(`customers/${customerId}/webhook-events`, {
        method: 'GET',
        version: '/v3',
      });
      const webhookEvents = webhookEventsResponse?.results;
      this.setStoreVal('webhookEvents', webhookEvents);
      return webhookEvents;
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: "Could not retrieve customer's events" },
      });
    }
  },

  async fetchCustomerSponsorEvents(customerId) {
    try {
      const webhookEventsResponse = await FetchHelper.fetchJson(`customers/${customerId}/sponsor-api-events`, {
        method: 'GET',
        version: '/qa',
      });
      const webhookEvents = webhookEventsResponse?.results;
      this.setStoreVal('sponsorEvents', webhookEvents);
      return webhookEvents;
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: "Could not retrieve customer's events" },
      });
    }
  },

  fetchCustomers({ multiPropsSearch, pageNumber = 1, sort }) {
    try {
      const queryParameters = { pageNumber, pageSize: 50 };

      if (multiPropsSearch) queryParameters.multiPropsSearch = multiPropsSearch;

      if (sort && sort.length > 0)
        queryParameters.sort = sort.map(({ property, direction }) => property + '|' + direction);

      const queryParametersString = Object.keys(queryParameters)
        .map((k) => k + '=' + queryParameters[k])
        .join('&');

      return FetchHelper.fetchJson(encodeURI(`customers?${queryParametersString}`), { method: 'GET', version: '/v2' });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not retrieve Customers' },
      });
      return null;
    }
  },

  async fetchCustomersV3(data) {
    try {
      return await FetchHelper.fetchJson(encodeURI(`customers?statuses=${data.status}`), {
        method: 'GET',
        version: '/v3',
      });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not retrieve Customers' },
      });
      return null;
    }
  },

  async setCustomer(data) {
    if (
      this.getStoreVal('customer') &&
      this.getStoreVal('customer').preSolarRate1 &&
      data?.preSolarRate1 !== this.getStoreVal('customer').preSolarRate1
    ) {
      await this.dispatch({ type: 'fetchProposal', data: this.getStoreVal('proposal').id });
      await this.dispatch({ type: 'refreshFinancingMethods' });
    }
    this.setStoreVal('defaultServiceAddressType', RESIDENTIAL);
    return this.setStoreVal('customer', data);
  },

  async setCustomerResults(results) {
    const data = results[0];
    return this.dispatch({ type: 'setCustomer', data });
  },

  saveEligibility(data) {
    this.setStoreValWithCheck('eligibility', data);
  },

  async submitEligibility({ customerId, eligibilityFormFields }) {
    try {
      eligibilityFormFields = {
        ...eligibilityFormFields,
        jointApplication: eligibilityFormFields.coapplicantSignature ?? false,
      };
      this.incrementLock('updateCustomer');
      // submit eligibility
      let response = null;
      try {
        response = await FetchHelper.fetchJson(`customers/${customerId}/eligibilities/in-person`, {
          method: 'post',
          version: '/v3',
          body: ApiRequestHelper.buildV3EligibilityData({ eligibilityFormData: { ...eligibilityFormFields } }),
        });
      } catch (e) {
        this.decrementLock('updateCustomer');
        throw e;
      }

      this.decrementLock('updateCustomer');

      if (response.results && response.results.length > 0) {
        this.dispatch({ type: 'saveEligibility', data: response.results[0] });
      }

      // calculations have changed due to eligibility
      this.dispatch({ type: 'refreshFinancingMethods' });

      this.dispatch({ type: 'setRoute', data: `/proposal/${this.getStoreVal('proposal').id}/eligibilityPending` });

      // fetching updated customer after submitting for eligibilities
      this.dispatch({ type: 'fetchCustomer', data: customerId });
    } catch (error) {
      if (error.response.status === 400) {
        error.response
          .clone()
          .json()
          .then((response) => {
            if (response && Array.isArray(response.messages)) {
              this.dispatch({ type: 'setErrorMessages', data: response.messages });
            } else {
              this.dispatch({ type: 'setErrorMessages', data: ['Please enter required information'] });
            }
          });
      } else {
        this.dispatch({
          type: 'handleRequestError',
          data: { error, defaultErrorMessage: 'Could not submit for eligibility' },
        });
      }
      throw error;
    }
  },

  async submitEligibilityByFinanceManager({ customerId, customerFormFields, eligibilityFormFields }) {
    try {
      const { csecondaryemail } = customerFormFields;
      eligibilityFormFields.applicantSignature = true;

      if (csecondaryemail) {
        eligibilityFormFields = {
          ...eligibilityFormFields,
          jointApplication: true,
          coapplicantSignature: true,
        };
      }
      // submit eligibility
      const response = await FetchHelper.fetchJson(`customers/${customerId}/eligibilities/by-finance-manager`, {
        method: 'post',
        body: ApiRequestHelper.buildEligibilityData({
          customerFormData: customerFormFields,
          eligibilityFormData: { ...eligibilityFormFields },
        }),
      });

      if (response.results && response.results.length > 0) {
        this.dispatch({ type: 'saveEligibility', data: response.results[0] });
      }

      // calculations have changed due to eligibility
      this.dispatch({ type: 'refreshFinancingMethods' });

      this.dispatch({ type: 'setRoute', data: `/proposal/${this.getStoreVal('proposal').id}/eligibilityStatus` });

      // fetching updated customer after submitting for eligibilities
      this.dispatch({ type: 'fetchCustomer', data: customerId });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not submit for eligibility' },
      });
      throw error;
    }
  },

  async submitHomeownerEligibility({ customerFormFields, eligibilityFormFields, consentToken }) {
    try {
      const { coapplicantSignature } = eligibilityFormFields;

      if (coapplicantSignature) {
        eligibilityFormFields = {
          ...eligibilityFormFields,
          jointApplication: true,
        };
      }
      this.incrementLock('updateCustomer');

      // submit eligibility
      let response = null;

      try {
        response = await FetchHelper.fetchJson(`homeowner/eligibilities/in-person/${consentToken}`, {
          method: 'post',
          body: ApiRequestHelper.buildEligibilityData({
            customerFormData: customerFormFields,
            eligibilityFormData: { ...eligibilityFormFields },
          }),
        });
      } catch (e) {
        this.decrementLock('updateCustomer');
        throw e;
      }

      this.decrementLock('updateCustomer');

      if (response.results && response.results.length > 0) {
        this.dispatch({ type: 'saveEligibility', data: response.results[0] });
      }
      this.dispatch({
        type: 'setRoute',
        data: '/homeowner/consent',
      });
      //}
    } catch (error) {
      if (error.response.status === 400) {
        error.response
          .clone()
          .json()
          .then((response) => {
            if (response && Array.isArray(response.messages)) {
              this.dispatch({ type: 'setErrorMessages', data: response.messages });
            } else {
              this.dispatch({ type: 'setErrorMessages', data: ['Please enter required information'] });
            }
          });
      } else {
        this.dispatch({
          type: 'handleRequestError',
          data: { error, defaultErrorMessage: 'Could not submit for eligibility' },
        });
      }
      throw error;
    }
  },

  async submitEligibilityUFO({ customerId, customerFormFields, eligibilityFormFields, billBeforeAgree }) {
    let financingPromise;
    this.incrementLock('updateCustomer');
    try {
      const response = await FetchHelper.fetchJson(`customers/${customerId}/eligibilities/in-person`, {
        method: 'post',
        body: ApiRequestHelper.buildCheckTitleEligibilityData({
          customerFormData: customerFormFields,
          eligibilityFormData: { ...eligibilityFormFields },
        }),
      });

      if (response.results && response.results.length > 0) {
        this.dispatch({ type: 'saveEligibility', data: response.results[0] });
      }

      // calculations have changed due to eligibility
      this.dispatch({ type: 'refreshFinancingMethods' });

      const financingMethodId = this.getStoreVal('financingMethod').id;
      const proposalId = this.getStoreVal('proposal').id;

      this.dispatch({ type: 'setRoute', data: `/proposal/${proposalId}/generating-documents` });

      financingPromise = this.dispatch({
        type: 'createFinancingRequest',
        data: { proposalId, financingMethodId },
      }).then((results) => {
        this.dispatch({ type: 'fetchProposal', data: proposalId }).then(() => {
          let subPage = billBeforeAgree ? 'step-documents-external' : 'step-documents';
          this.dispatch({
            type: 'goToProposalPage',
            data: results === undefined ? undefined : { subPage },
          });

          this.dispatch({ type: 'fetchCustomer', data: customerId });
        });
      });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not submit for title eligibility' },
      });
      throw error;
    }
    this.decrementLock('updateCustomer');
    return financingPromise;
  },

  async submitPaperCredit({ customerId, uploadFile }) {
    try {
      const path = `customers/${customerId}/paper-credit`;
      const paperCreditResponse = await FetchHelper.uploadFile({
        path,
        uploadFile,
        method: 'POST',
      });

      if (paperCreditResponse && paperCreditResponse.body.results.length > 0) {
        this.dispatch({
          type: 'saveEligibility',
          data: paperCreditResponse.body.results[0],
        });
      }

      this.dispatch({ type: 'setRoute', data: `/proposal/${this.getStoreVal('proposal').id}` });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: {
          error,
          defaultErrorMessage:
            error.response.body.messages || 'Something went wrong. Please try uploading your file again.',
        },
      });
    }
  },

  async deleteCustomer(customerId) {
    await FetchHelper.fetchJson(`customers/${customerId}`, { method: 'delete' });
  },

  async deleteStipDocuments({ customerId, documentIds, onSuccess, onError }) {
    let operations = [];
    documentIds.forEach((documentId) => {
      operations.push({ op: 'delete', resource: documentId });
    });
    let fetchPromise = FetchHelper.fetchJson(`customers/${customerId}/customer-documents`, {
      body: { operations },
      method: 'DELETE',
    });

    fetchPromise
      .then((response) => {
        if (response.status === 'success' && typeof onSuccess === 'function') {
          onSuccess();
          Promise.all([this.dispatch({ type: 'fetchCreditDocuments', data: customerId })]);
        }
      })
      .catch((error) => {
        if (typeof onError === 'function') {
          onError(error);
        } else {
          throw error;
        }
      });
  },

  async completeStips({ creditCheckId }) {
    const consentToken = this.getStoreVal('view')?.token;
    const customerId = this.getStoreVal('customer')?.id;

    if (consentToken) {
      FetchHelper.fetchJson(`homeowner/credit/${consentToken}/credit-checks/${creditCheckId}/stips`, {
        body: { creditCheckId: creditCheckId },
        method: 'POST',
      });
    } else {
      FetchHelper.fetchJson(`customers/${customerId}/credit-checks/submit-stips`, {
        method: 'POST',
        version: '/v3',
      });
      await this.dispatch({ type: 'fetchEligibility', data: { customerId, consentToken } });
    }
  },

  async getCreditTasks() {
    const consentToken = this.getStoreVal('view')?.token;
    const creditCheckId = this.getStoreVal('customer')?.currentCreditCheckId;

    if (consentToken) {
      let homeownertasks = await FetchHelper.fetchJson(
        `homeowner/credit/${consentToken}/credit-checks/${creditCheckId}/tasks`,
        {
          method: 'GET',
        }
      );
      this.setStoreVal('creditCheckTasks', homeownertasks);
      return;
    }
    let tasks = await FetchHelper.fetchJson(`customers/credit-checks/${creditCheckId}/tasks`, {
      method: 'GET',
    });
    this.setStoreVal('creditCheckTasks', tasks);
  },

  async persistCustomerStatusUpdate({ id, status }) {
    try {
      await FetchHelper.fetchJson(`customers/${id}`, {
        method: 'put',
        body: {
          status,
        },
      });
      this.dispatch({ type: 'setMessages', data: ['Customer status updated'] });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Failed to update customer status' },
      });
      throw error;
    }
  },

  async setCustomerListFinancingRequestId({ financingRequestId }) {
    await this.setStoreVal('customerListFinancingRequestId', financingRequestId);
  },

  async setModalDataEntryOnSubmit(data) {
    await this.setStoreVal('modalDataEntryOnSubmit', data);
  },

  async modalDataEntryOnSubmit() {
    const onSubmit = await this.getStoreVal('modalDataEntryOnSubmit');
    return onSubmit();
  },

  async eligibilityDelete({
    customerId,
    proposalId,
    financingRequestId,
    legalDocuments,
    reload = true,
    afterHandoff = false,
    force = false,
    cancellationReason,
  }) {
    try {
      if (financingRequestId !== null) {
        await this.dispatch({
          type: 'deleteFinancingRequest',
          data: { customerId, financingRequestId, afterHandoff, force, cancellationReason },
        });
      }

      if (legalDocuments !== null && legalDocuments.length > 0) {
        await this.dispatch({ type: 'legalDocumentsDelete', data: { proposalId } });
      }

      await this.dispatch({ type: 'fetchProposal', data: proposalId }); // to sync Proposal FR properties

      await FetchHelper.fetchJson(`customers/${customerId}/eligibilities`, { method: 'delete' });
      await this.dispatch({ type: 'fetchCustomer', data: customerId });
      this.dispatch({ type: 'clearEligibility', data: { reload } });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not delete eligibility' },
      });
      throw error;
    }
    this.dispatch({ type: 'goToProposalPage', data: { subPage: 'address', proposalId } });
  },

  async submitEligibilityEmail({ customerId, customerFormValues }) {
    if (!this.acquireLock('submitEligibilityEmail')) {
      return false;
    }
    try {
      let customerData = ApiRequestHelper.buildCustomerFormData(customerFormValues);
      customerData.salesRepConsented = true;

      await this.dispatch({ type: 'updateCustomer', data: { customerId, body: customerData } });

      const response = await FetchHelper.fetchJson(`customers/${customerId}/eligibilities/by-email`, {
        method: 'post',
        version: '/v3',
      });

      if (response && response.results && response.results.length) {
        this.dispatch({ type: 'saveEligibility', data: response.results[0] });
      }

      this.dispatch({ type: 'goToProposalPage', data: { subPage: 'eligibilityStatus' } });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not submit for eligibility by email' },
      });
      throw error;
    }
    this.releaseLock('submitEligibilityEmail');
    return true;
  },

  async fetchPlaidLink() {
    const token = this.getStoreVal('view')?.token;
    const response = await FetchHelper.fetchJson(
      `customers/token/${token}/autopay/token?role=${LocationHelper.searchAsObj().role}`,
      {
        method: 'get',
        version: '/v3',
        headers: {
          authorization: AuthHelper.welcomeCallTokenAuth(),
        },
      }
    );

    this.setStoreVal('autoPayData', response?.results?.[0]);
    return response;
  },

  async fetchAutoPay(params) {
    const response = await FetchHelper.fetchJson(
      this.getStoreVal('view')?.token
        ? `customers/token/${this.getStoreVal('view')?.token}/autopay?role=${LocationHelper.searchAsObj().role}`
        : `customers/${params?.customerId}/autopay`,
      {
        method: 'get',
        version: '/v3',
        headers: {
          authorization: AuthHelper.welcomeCallTokenAuth(),
        },
      }
    );

    let val = response?.results?.[0];
    this.setStoreVal('autoPayData', val);

    return val;
  },

  async fetchPlaidAccessLink({ publicToken }) {
    const response = await FetchHelper.fetchJson(
      `customers/token/${this.getStoreVal('view')?.token}/autopay/token/${publicToken}?role=${
        LocationHelper.searchAsObj().role
      }`,
      {
        method: 'get',
        version: '/v3',
        headers: {
          authorization: AuthHelper.welcomeCallTokenAuth(),
        },
      }
    );

    let val = response?.results?.[0];
    this.setStoreVal('autoPayData', val);
    return val;
  },

  async setAutoPayOptIn({ customerId, flag }) {
    const response = await FetchHelper.fetchJson(
      `customers/token/${this.getStoreVal('view')?.token}/autopay/opt-in-preference/${flag}`,
      {
        method: 'post',
        version: '/v3',
        headers: {
          authorization: AuthHelper.welcomeCallTokenAuth(),
        },
      }
    );

    let val = response?.results?.[0];
    this.setStoreVal('autoPayData', val);
    return val;
  },

  async setEnrollmentStatus({ customerId, flag }) {
    const response = await FetchHelper.fetchJson(
      `customers/token/${this.getStoreVal('view')?.token}/autopay/enrollment-status/${flag}`,
      {
        method: 'post',
        version: '/v3',
        headers: {
          authorization: AuthHelper.welcomeCallTokenAuth(),
        },
      }
    );

    const val = response?.results?.[0];
    this.setStoreVal('autoPayData', val);
    return val;
  },

  async logAutoPayEvent({ customerId, type, body, applicantRole }) {
    const response = await FetchHelper.fetchJson(
      `customers/token/${this.getStoreVal('view')?.token}/autopay/events/${type}?role=${applicantRole}`,
      {
        method: 'post',
        version: '/v3',
        headers: {
          authorization: AuthHelper.welcomeCallTokenAuth(),
        },
        body: JSON.stringify(body, null, 2),
      }
    );

    return response?.results?.[0];
  },

  async fetchEligibility({ customerId, consentToken }) {
    try {
      const { results } = await FetchHelper.fetchJson(
        consentToken ? `/homeowner/eligibilities/in-person/${consentToken}` : `customers/${customerId}/eligibilities`,
        { method: 'get' }
      );

      if (JSON.stringify(this.getStoreVal('eligibility')) !== JSON.stringify(results?.[0])) {
        if (results && results.length) {
          const eligibility = results[0];
          const preExistingEligibility = this.getStoreVal('eligibility');

          switch (eligibility.overallStatus) {
            case 'approved':
              this.setStoreVal('eligibilitySection', 'success');
              break;
            case 'failed':
              this.setStoreVal('eligibilitySection', 'failure');
              break;
            case 'initiated':
              this.setStoreVal('eligibilitySection', 'initiated');
              break;
            case 'pended':
              this.setStoreVal('eligibilitySection', 'pended');
              break;
            default:
              break;
          }

          // when running credit check, and we get back a "completed" eligibility, then refresh the FM, since for RBP, the FM.fcm_id can change after credit
          if (
            preExistingEligibility != null &&
            ['initiated', 'incomplete'].includes(preExistingEligibility?.overallStatus) &&
            !['initiated', 'incomplete'].includes(eligibility?.overallStatus)
          ) {
            await this.dispatch({ type: 'refreshFinancingMethods' });
          }

          await this.dispatch({ type: 'saveEligibility', data: eligibility });
        }
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not get eligibility results' },
      });
    }
  },

  async fetchPrescreen(customerId) {
    try {
      const prescreenResponse = await FetchHelper.fetchJson(`customers/${customerId}/prescreen`, {
        method: 'get',
      });
      const { results } = prescreenResponse;
      if (results && results.length) {
        this.setStoreVal('prescreenProducts', results);
        return results;
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not get prescreen results' },
      });
    }
  },

  clearPrescreen() {
    this.setStoreVal('prescreenProducts', null);
  },

  async fetchProperty(customerId) {
    try {
      const propertyResponse = await FetchHelper.fetchJson(`customers/${customerId}/property-infos`, {
        method: 'GET',
      });
      const { results } = propertyResponse;
      let propertyInfo = null;

      if (results && results.length) {
        propertyInfo = results[0];
      }

      this.setStoreVal('propertyInfo', propertyInfo);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not get propertyInfo results' },
      });
    }
  },

  async preScreenCustomer(customerData) {
    try {
      this.dispatch({ type: 'clearProposal' });

      //hit v3 customer/proposal/prescreen endpoint
      let preScreenResponse = await FetchHelper.fetchJson('prescreen-customers', {
        method: 'post',
        version: '/v3',
        body: {
          ...{
            cssn: EligiblityHelper.valueOrNull(customerData?.[applicantEligibility?.social]),
            cdob: DateHelper.convertUsToIsoDate(customerData?.[applicantEligibility?.dateOfBirth]),
            cgrossannualincome: EligiblityHelper.valueOrNull(customerData?.[applicantEligibility?.annualIncome]),
            csecondaryssn: EligiblityHelper.valueOrNull(customerData?.[coApplicantEligibility?.social]),
            csecondarydob: DateHelper.convertUsToIsoDate(customerData?.[coApplicantEligibility?.dateOfBirth]),
            csecondarygrossannualincome: EligiblityHelper.valueOrNull(
              customerData?.[coApplicantEligibility?.annualIncome]
            ),
            defaultServiceAddressType: RESIDENTIAL,
          },
          ...ObjectHelper.pick(
            customerData,
            applicant.firstName,
            applicant.lastName,
            applicant.address,
            applicant.phone,
            applicant.email,
            applicant.thirdPartyLeadExternalSku,
            applicant.thirdPartyLeadExternalId,

            applicant.campaignId,

            applicant.city,
            applicant.state,
            applicant.zip,

            applicant.source,

            coApplicant.firstName,
            coApplicant.lastName,

            coApplicant.email,
            coApplicant.phone
          ),
        },
      });

      const userAcls = this.getStoreVal('userFunctionalityAcls');
      if (FunctionalityAccessHelper.hasHideValueProps(userAcls)) {
        await FetchHelper.fetchJson(`customers/${preScreenResponse.results[0].id}/financing-methods`, {
          method: 'PATCH',
          version: '/v3',
          body: { amountFinancedOverride: true, dollarPerWatt: null },
        });
      }

      const result = preScreenResponse.results[0];

      this.setStoreVal('prescreenProducts', result.preScreenProductAtom ? [result.preScreenProductAtom] : []);
      this.setStoreVal('proposal', result.proposal);
      this.setStoreVal('customer', result);
      return {
        customerId: result.id,
        proposalId: result.proposal.id,
        financingMethodId: result.proposal.financingMethods[0].id,
      };
    } catch (error) {
      console.error('prescreenError', error);
      this.dispatch({
        type: 'handleRequestError',
        data: {
          error,
          defaultErrorMessage:
            'Quick Check is not Available at this time. Please try again, or click Go Back to start a new proposal.',
        },
      });
    }
    return null;
  },

  async createThirdPartyLead(data) {
    try {
      await FetchHelper.fetchJson('third-party-leads', {
        method: 'post',
        version: '/v3',
        body: {
          externalSku: data.externalSku,
          externalId: data.externalId,
          customerId: data.customerId,
        },
      });
    } catch (error) {
      console.error('thirdPartyLeadError', error);
      this.dispatch({
        type: 'handleRequestError',
        data: {
          error,
          defaultErrorMessage: 'Third party lead failed.',
        },
      });
    }
    return null;
  },

  setCustomerFormValid(valid) {
    this.setStoreVal('customerFormValid', valid);
  },

  setUserInputLoanAmountIsInvalid(userInputLoanAmountIsInvalid) {
    this.setStoreVal('userInputLoanAmountIsInvalid', userInputLoanAmountIsInvalid);
  },

  async sendHomeownerStips({ customerId, stipReqIds }) {
    try {
      const eligibilitiesResponse = await FetchHelper.fetchJson(
        `customers/${customerId}/eligibilities/send-homeowner-stips`,
        {
          method: 'put',
          body: stipReqIds,
        }
      );

      if (eligibilitiesResponse.status === 'fail') {
        this.dispatch({ type: 'setErrorMessages', data: eligibilitiesResponse.messages });
      } else {
        this.dispatch({ type: 'setMessages', data: eligibilitiesResponse.messages });
      }
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Failed to send email to homeowner' },
      });
    }
  },

  async triggerAddressInfo({ addressInfoMethod }) {
    this.setStoreVal('previousAddressInfoMethod', this.getStoreVal('addressInfoMethod'));
    this.setStoreVal('addressInfoMethod', addressInfoMethod);
  },

  triggerUtilityAccountInfo({ utilityAccountInfoMethod }) {
    this.setStoreVal('utilityAccountInfoMethod', utilityAccountInfoMethod);
  },

  toggleCoApplicantForm(data) {
    this.setStoreVal('displayCoApplicantSection', data);
  },

  startEditModeReview() {
    this.mergeStoreVal('locks', { editModeReview: 1 });
  },

  endEditModeReview() {
    this.mergeStoreVal('locks', { editModeReview: 0 });
  },

  async fetchCreditDocuments(customerId) {
    const url = `customers/${customerId}/customer-documents`;
    const errorMessage = 'Failed to fetch customer documents';

    let response;
    try {
      response = await FetchHelper.fetchJson(url, { method: 'GET' });
      this.setStoreValWithCheck('creditDocuments', response.results);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: {
          error,
          defaultErrorMessage: errorMessage,
        },
      });
      throw error;
    }
    return response;
  },

  async fetchStipStatusNotes({ customerId, token }) {
    const url = `customers/${token ? `token/${token}` : customerId}/stips/status-notes`;
    const storeKey = 'stipStatusNotesArr';
    const errorMessage = 'Failed to fetch stip Status Notes';

    let hasStips = EligiblityHelper.hasCreditStips(this.getStoreVal('eligibility'));

    let response;
    if ((Array.isArray(hasStips) && hasStips.length > 0) || (typeof hasStips === 'boolean' && hasStips) || token) {
      try {
        let options = { method: 'GET' };
        response = await FetchHelper.fetchJson(url, options);
        if (storeKey) this.setStoreVal(storeKey, response.results);
      } catch (error) {
        this.dispatch({
          type: 'handleRequestError',
          data: {
            error,
            defaultErrorMessage: errorMessage,
          },
        });
        throw error;
      }
    }
    return response;
  },

  async clearPaymentPageConfig() {
    this.setStoreVal('paymentPageConfig', null);
  },

  async fetchVerification({ customerId, callback }) {
    const url = `customers/${customerId}/verifications`;
    const errorMessage = 'Failed to fetch customer verification token';

    let response;
    try {
      let options = { method: 'GET' };
      response = await FetchHelper.fetchJson(url, options);
      if (callback) callback(response.results);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: {
          error,
          defaultErrorMessage: errorMessage,
        },
      });
      throw error;
    }
    return response;
  },
  fetchCustomerBannerData({ customerId, status }) {
    try {
      const holdCategorySku = this.getStoreVal('customer')?.holdCategorySku;
      if (status?.toLowerCase()?.includes('cancelled') || holdCategorySku != null) {
        return FetchHelper.fetchJson(`customers/${customerId}/audit-trail/banner`, {
          method: 'GET',
          version: '/v3',
        }).then(({ results }) => {
          this.setStoreVal('customerBannerData', results);
        });
      }
      this.setStoreVal('customerBannerData', []);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not fetch customer audit trail' },
      });
    }
  },
  clearCustomerBannerData() {
    this.setStoreVal('customerBannerData', null);
  },

  async goToCustomerProposal({ customerId, newTab }) {
    const url = `${LocationHelper.getUrlForSubdomain('switch')}/customer/${customerId}/proposal`;
    if (newTab) {
      let child = window.open(url, '_blank');
      child.focus();
    } else {
      await this.dispatch({ type: 'fetchAuthenticatedUser' });
      this.dispatch({ type: 'setRoute', data: url });
    }
  },

  async fetchCustomerHoldTypes() {
    try {
      const results = (
        await FetchHelper.fetchJson(`customers/hold-types`, {
          method: 'GET',
          version: '/v1',
        })
      )?.results;
      this.setStoreVal('customerHoldTypes', results);
      return results;
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not fetch customer hold types' },
      });
    }
  },

  async submitMeasures(data) {
    let measures = await FetchHelper.fetchJson(
      `customers/${data?.customerId}/measure-categories/${data?.measureCategory}`,
      {
        method: 'PUT',
        version: '/v3',
        body: data?.measures,
      }
    );
    this.setStoreVal('customerMeasures', measures?.results);
    return measures?.results;
  },

  async retrieveMeasures(data) {
    let measures = await FetchHelper.fetchJson(`customers/${data?.customerId}/measures`, {
      method: 'GET',
      version: '/v3',
    });
    this.setStoreVal('customerMeasures', measures?.results);
    return measures?.results;
  },

  async fetchCustomerInverterMonitoring({ customerId }) {
    let promise = await FetchHelper.fetchJson(`customers/${customerId}/financing-requests/system-monitoring`, {
      version: '/v3',
    });

    if (promise?.results) {
      this.setStoreValWithCheck('customerInverterMonitoring', promise.results);
    }
    return promise?.results;
  },
};

export default CustomerDispatcher;
